/**
 * skyAgent inline approval card (SkyCRM) — ported from skySchedule's
 * `SchedulingActionCard`, re-skinned onto SkyPoint components.
 *
 * BUILT NOW, WIRED LATER (Track B brief). Increment 2 is the READ-ONLY agent, so
 * there is no action endpoint yet and Contract B's route table does not define
 * one. This component is therefore deliberately PRESENTATIONAL: it takes the
 * action and its handlers as props and performs no data fetching. skySchedule's
 * version owns its own react-query calls to `/agent-actions/:id`; reproducing
 * that here would mean inventing an unratified endpoint, so the fetching half is
 * left for the increment that ratifies it.
 *
 * The approval posture it renders is ratified though (STATUS-skyagent.md D2):
 * every mutation is an approval card and nothing outbound sends without a human
 * click, so `onApprove` / `onReject` are the ONLY paths that may mutate, and a
 * user without permission sees a read-only pending state rather than buttons.
 */
import {
  SkyButton,
  SkyBody1,
  SkyCard,
  SkyDateTimePicker,
  SkyInput,
  SkyLoader,
  SkyTextarea,
} from "@skypointcloud/skypoint-ui";
import { AlertTriangle, Ban, Check, ClipboardList, X } from "lucide-react";
import { Fragment, useState } from "react";
import type { AgentActionType } from "../../../../lib/enums/agentActionType";

/**
 * The action-type union moved to `lib/enums/agentActionType.ts` in Increment 3.
 *
 * It was declared here, and the server was about to declare its own copy — the
 * `stageType.ts` situation exactly. The drift is quiet rather than loud: the
 * server proposes a type the card has no label for, and `ACTION_LABELS[…] ??
 * "Proposed action"` below renders it without complaint, so a counselor is asked
 * to approve a write whose KIND they cannot see. Graceful degradation is what
 * lets that survive review, which is why the vocabulary is now shared instead.
 *
 * Re-exported so existing importers of this module are unchanged.
 */
export type { AgentActionType };

export type AgentActionStatus = "proposed" | "approved" | "executed" | "rejected" | "failed";

export interface AgentActionWarning {
  code: string;
  message: string;
}

/**
 * The shapes `dueDateValue()` (server/services/crmAgent/crmActionTools/actionKit.ts)
 * accepts back from an edit: `2026-08-20`, `2026-08-20 14:00`, `2026-08-20T14:00:00`.
 * An editable field whose PROPOSED value looks like this gets a date-time picker
 * instead of a text box.
 *
 * Inferred from the VALUE SHAPE rather than from a `type` on the editable
 * contract, which keeps Session 1's frozen `CrmActionSpec` untouched and covers
 * both `dueAt` and `scheduledAt` without either spec declaring anything.
 *
 * NO `minDateTime` IS PASSED, deliberately. The two fields that reach this branch
 * have OPPOSITE rules: a tour must be in the future, while a follow-up may
 * legitimately be back-dated — `readOverdueTasks` exists to surface exactly that,
 * and the card says "this will show as overdue straight away" rather than
 * refusing. One component cannot tell them apart from a value shape, so the floor
 * is enforced server-side per action, where the rule actually differs.
 */
export const WALL_CLOCK_VALUE = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?$/;

const pad2 = (n: number) => String(n).padStart(2, "0");

/**
 * Parse the payload's wall-clock string into a local Date for the picker.
 *
 * Deliberately NOT `new Date(str)`: an ISO-looking string with no zone is parsed as
 * UTC by the spec, which would render a 14:00 tour at 09:00 for a Central user. The
 * server stores and re-parses wall-clock, so the digits must survive untouched.
 * A date with no time takes 09:00 — the same default `dueDateValue()` applies.
 */
export function parseWallClock(value: string): Date | null {
  const m = WALL_CLOCK_VALUE.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d, hh, mi, ss] = m;
  const dt = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    hh === undefined ? 9 : Number(hh),
    Number(mi ?? 0),
    Number(ss ?? 0),
  );
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * Back to the wall-clock string the executor re-parses.
 *
 * `toISOString()` here would be the bug this whole helper exists to avoid: it
 * converts to UTC, so approving an unedited 14:00 tour in Central would write
 * 19:00 and nobody would see it until the family arrived at the wrong time.
 */
export function formatWallClock(d: Date): string {
  return (
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}` +
    `T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`
  );
}

export interface AgentActionPreview {
  title: string;
  lines: Array<{ label: string; value: string }>;
  /** `key` is the PAYLOAD FIELD an edit writes back to; the approve route merges
   *  by this name and validates against this same list. */
  editable?: Array<{ key: string; label: string; value: string }>;
}

export interface AgentAction {
  id: string;
  actionType: AgentActionType;
  status: AgentActionStatus;
  /** Server-rendered one-line description. Never assembled on the client. */
  summary: string;
  /** Present on a live proposal; absent on a terminal row rendered from history. */
  preview?: AgentActionPreview;
  warnings?: AgentActionWarning[];
  error?: string | null;
}

export interface AgentActionCardProps {
  action: AgentAction;
  /** False renders the read-only "pending approval" state instead of buttons. */
  canApprove?: boolean;
  /** True while a decision is in flight. */
  busy?: boolean;
  /** Edited values are passed back keyed by `editable[].key`, and ONLY the fields
   *  the human actually changed — so the server can record an edit rate that means
   *  something rather than one that counts every approval as an edit. */
  onApprove?: (actionId: string, edits?: Record<string, string>) => void;
  onReject?: (actionId: string) => void;
}

/**
 * Human labels for the action-type eyebrow. Never shows a raw action code.
 *
 * `Record<AgentActionType, string>` is load-bearing: adding a member to the shared
 * union without a label here is a TYPE error, which is what stops the silent
 * "Proposed action" fallback from becoming the normal way a new action renders.
 */
const ACTION_LABELS: Record<AgentActionType, string> = {
  propose_log_activity: "Log activity",
  propose_create_task: "Create task",
  propose_update_stage: "Update pipeline stage",
  propose_draft_message: "Draft message",
  propose_schedule_tour: "Schedule tour",
  propose_remember: "Remember this",
  // "Save as draft", not "Save draft" — it has to read as clearly different from
  // "Draft message" two rows up, which is the SEND flow. Two cards a counselor
  // sees in the same thread must not be distinguishable only by word order.
  propose_save_draft: "Save as draft",
};

export function AgentActionCard({
  action,
  canApprove = false,
  busy = false,
  onApprove,
  onReject,
}: AgentActionCardProps) {
  const { id, actionType, status, summary, error } = action;
  const warnings = action.warnings ?? [];
  const pending = status === "proposed";
  const preview = action.preview;
  const editableFields = pending && canApprove ? (preview?.editable ?? []) : [];

  /**
   * Local edits, keyed by payload field. Seeded from the proposal and reset when a
   * DIFFERENT action lands in this slot — keying the state on the action id rather
   * than syncing in an effect, so a card that re-renders while someone is typing
   * cannot have their draft overwritten by a refetch of the same proposal.
   */
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [editedFor, setEditedFor] = useState<string | null>(null);
  if (editedFor !== id) {
    setEditedFor(id);
    setEdits({});
  }

  const valueOf = (field: { key: string; value: string }) => edits[field.key] ?? field.value;

  /**
   * Only the fields that actually CHANGED. RFP §6.2 wants an edit rate that
   * distinguishes real review from rubber-stamping; sending every field back
   * whenever the form was rendered would report 100% edited and be exactly as
   * useless as reporting 0%.
   */
  const changedEdits = () => {
    const out: Record<string, string> = {};
    for (const field of preview?.editable ?? []) {
      const next = edits[field.key];
      if (next !== undefined && next !== field.value) out[field.key] = next;
    }
    return Object.keys(out).length > 0 ? out : undefined;
  };

  return (
    <SkyCard className="mt-2 overflow-hidden border border-amber-500/30">
      <div className="flex items-start gap-3 p-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400">
          <ClipboardList
            className="h-4 w-4"
            aria-hidden="true"
          />
        </span>

        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
            {ACTION_LABELS[actionType] ?? "Proposed action"}
          </div>
          <SkyBody1 className="mt-0.5 block">{preview?.title || summary}</SkyBody1>

          {/*
            What will actually happen, as the server described it. A definition
            list rather than prose: the counselor is checking facts (which
            prospect, which community, which time), and prose makes them read
            rather than scan.
          */}
          {preview && preview.lines.length > 0 && (
            <dl className="mt-2 grid grid-cols-[minmax(0,7rem)_1fr] gap-x-3 gap-y-1 text-sm">
              {preview.lines.map((line) => (
                <Fragment key={`${line.label}-${line.value}`}>
                  <dt className="truncate text-muted-foreground">{line.label}</dt>
                  <dd className="min-w-0 whitespace-pre-wrap break-words">{line.value}</dd>
                </Fragment>
              ))}
            </dl>
          )}

          {/*
            The editable half. This is the human-in-the-loop the RFP sells: a
            counselor who can only approve or reject has to reject and re-ask to
            change one word, so they approve near-misses instead — and the edit
            rate then reports a rubber stamp that is really a missing affordance.
          */}
          {editableFields.length > 0 && (
            <div className="mt-3 space-y-2">
              {editableFields.map((field) => {
                const long = field.value.length > 80;
                const inputId = `agent-action-${id}-${field.key}`;

                // A when-field gets a real picker. The TYPE is decided from the
                // PROPOSED value, never the edited one — deriving it from
                // `valueOf(field)` would swap the control out from under someone
                // the moment they cleared the box.
                if (WALL_CLOCK_VALUE.test(field.value.trim())) {
                  return (
                    <div key={field.key}>
                      <SkyDateTimePicker
                        label={field.label}
                        value={parseWallClock(valueOf(field))}
                        onSelectDateTime={(d) =>
                          setEdits((prev) => ({
                            ...prev,
                            [field.key]: d ? formatWallClock(d) : "",
                          }))
                        }
                      />
                    </div>
                  );
                }

                return (
                  <div key={field.key}>
                    <label
                      htmlFor={inputId}
                      className="mb-1 block text-sm font-medium text-muted-foreground"
                    >
                      {field.label}
                    </label>
                    {long ? (
                      <SkyTextarea
                        id={inputId}
                        rows={4}
                        value={valueOf(field)}
                        disabled={busy}
                        onChange={(e) =>
                          setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                      />
                    ) : (
                      <SkyInput
                        id={inputId}
                        value={valueOf(field)}
                        disabled={busy}
                        onChange={(e) =>
                          setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {warnings.length > 0 && pending && (
            <ul className="mt-2 space-y-1">
              {warnings.map((w) => (
                <li
                  key={w.code}
                  className="flex items-start gap-1.5 text-sm text-amber-700 dark:text-amber-400"
                >
                  <AlertTriangle
                    className="mt-0.5 h-3 w-3 shrink-0"
                    aria-hidden="true"
                  />
                  <span>{w.message}</span>
                </li>
              ))}
            </ul>
          )}

          {/*
            Terminal states. Status is carried by an icon AND a word, never by
            colour alone, and never in brand amber (design rule: status uses the
            danger/success/neutral roles).
          */}
          {status === "executed" && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-400">
              <Check
                className="h-3.5 w-3.5"
                aria-hidden="true"
              />{" "}
              Applied
            </div>
          )}
          {status === "approved" && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-400">
              <Check
                className="h-3.5 w-3.5"
                aria-hidden="true"
              />{" "}
              Approved
            </div>
          )}
          {status === "rejected" && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
              <X
                className="h-3.5 w-3.5"
                aria-hidden="true"
              />{" "}
              Rejected
            </div>
          )}
          {status === "failed" && (
            <div className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-destructive">
              <AlertTriangle
                className="h-3.5 w-3.5"
                aria-hidden="true"
              />
              {error ? `Could not apply: ${humanizeActionError(error)}` : "Could not apply"}
            </div>
          )}

          {pending &&
            (canApprove ? (
              <div className="mt-3 flex items-center gap-2">
                <SkyButton
                  appearance="primary"
                  size="small"
                  disabled={busy}
                  onClick={() => onApprove?.(id, changedEdits())}
                >
                  {busy ? (
                    <SkyLoader size="tiny" />
                  ) : (
                    <Check
                      className="h-3.5 w-3.5"
                      aria-hidden="true"
                    />
                  )}
                  Approve
                </SkyButton>
                <SkyButton
                  appearance="transparent"
                  size="small"
                  disabled={busy}
                  onClick={() => onReject?.(id)}
                >
                  <X
                    className="h-3.5 w-3.5"
                    aria-hidden="true"
                  />{" "}
                  Reject
                </SkyButton>
              </div>
            ) : (
              <div className="mt-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                <Ban
                  className="h-3.5 w-3.5"
                  aria-hidden="true"
                />{" "}
                Pending approval
              </div>
            ))}
        </div>
      </div>
    </SkyCard>
  );
}

/** Map terse server codes to plain language. Mirrors the skySchedule helper. */
export function humanizeActionError(raw?: string): string {
  const s = (raw ?? "").toLowerCase();
  if (s.includes("expired") || s.includes("410")) {
    return "this proposal expired, ask skyAgent to propose it again";
  }
  if (s.includes("already")) return "this action was already decided";
  if (s.includes("consent") || s.includes("do_not")) {
    return "the contact preferences on this record block that send";
  }
  if (s.includes("read_only") || s.includes("readonly")) {
    return "this tenant is read only";
  }
  return raw || "something went wrong";
}
